// client/src/components/PasswordReset.jsx
import { useState } from 'react';
import { supabase } from '../utils/supabaseClient';

const USERNAME_REGEX = /^[a-z0-9_.]+$/;

export default function AuthForm() {
  const [mode, setMode] = useState('login'); // login | signup | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setMessage('');
    if (next === 'reset') setPassword('');
  };

  const normalizeUsername = (raw) => raw.trim().toLowerCase();

  const validateUsername = () => {
    const normalized = normalizeUsername(username || '');
    if (!normalized) return { ok: false, error: 'Please choose a username.' };
    if (normalized.length < 3 || normalized.length > 32) {
      return { ok: false, error: 'Username must be 3-32 characters.' };
    }
    if (!USERNAME_REGEX.test(normalized)) {
      return { ok: false, error: 'Use letters, numbers, underscores, or dots only.' };
    }
    return { ok: true, value: normalized };
  };

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setMessage('Email and password are required.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setMessage(error ? error.message : 'Signed in! Redirecting...');
    setLoading(false);
  };

  const handleSignup = async () => {
    const usernameCheck = validateUsername();
    if (!usernameCheck.ok) {
      setMessage(usernameCheck.error);
      return;
    }
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setMessage('Email is required.');
      return;
    }
    if (!password || password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          username: usernameCheck.value,
        },
      },
    });
    if (error) {
      const msg = /username/i.test(error.message) || /duplicate/i.test(error.message)
        ? 'That username is already taken.'
        : error.message;
      setMessage(msg);
      setLoading(false);
      return;
    }
    const needsConfirmation = !data?.session;
    setMessage(needsConfirmation ? 'Check your email to confirm your account.' : 'Account created! Redirecting...');
    setLoading(false);
  };

  const handleReset = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setMessage('Email is required.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail);
    setMessage(error ? error.message : 'Password-reset email sent.');
    setLoading(false);
  };

  const handleSubmit = () => {
    if (mode === 'reset') return handleReset();
    if (mode === 'signup') return handleSignup();
    return handleLogin();
  };

  const buttonLabel = mode === 'reset' ? 'Send Reset Link' : mode === 'signup' ? 'Create Account' : 'Login';

  return (
    <div className="auth-form">
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          className={`auth-button ${mode === 'login' ? '' : 'auth-button-secondary'}`}
          onClick={() => switchMode('login')}
          disabled={loading}
        >
          Sign In
        </button>
        <button
          type="button"
          className={`auth-button ${mode === 'signup' ? '' : 'auth-button-secondary'}`}
          onClick={() => switchMode('signup')}
          disabled={loading}
        >
          Create Account
        </button>
        <button
          type="button"
          className={`auth-button ${mode === 'reset' ? '' : 'auth-button-secondary'}`}
          onClick={() => switchMode('reset')}
          disabled={loading}
        >
          Reset
        </button>
      </div>

      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="auth-input"
        autoComplete="email"
      />

      {mode === 'signup' && (
        <input
          type="text"
          placeholder="Username (unique)"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="auth-input"
          autoCapitalize="none"
          autoComplete="username"
        />
      )}

      {mode !== 'reset' && (
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth-input"
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
        />
      )}

      <button
        disabled={loading}
        onClick={handleSubmit}
        className="auth-button"
      >
        {loading ? 'Working...' : buttonLabel}
      </button>

      {mode === 'login' && (
        <p>
          <span onClick={() => switchMode('reset')} className="auth-link">
            Forgot password?
          </span>{' '}
          |{' '}
          <span onClick={() => switchMode('signup')} className="auth-link">
            Create an account
          </span>
        </p>
      )}

      {mode === 'signup' && (
        <p>
          <span onClick={() => switchMode('login')} className="auth-link">
            Already have an account? Sign in
          </span>
        </p>
      )}

      {mode === 'reset' && (
        <p>
          <span onClick={() => switchMode('login')} className="auth-link">
            Back to login
          </span>
        </p>
      )}

      {message && <p className="auth-message">{message}</p>}
    </div>
  );
}
